import React, { useEffect, useRef } from 'react';
import styles from './GenericModal.module.css';

/**
 * Button configuration for modal actions
 */
export interface ModalButton {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}

/**
 * GenericModal Props Interface
 */
export interface GenericModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Modal message/content */
  message?: string;
  /** Custom content (overrides message if provided) */
  children?: React.ReactNode;
  /** Array of action buttons */
  buttons?: ModalButton[];
  /** Visual variant for the modal */
  variant?: 'info' | 'warning' | 'error' | 'success';
  /** Show/hide close button */
  showCloseButton?: boolean;
  /** Optional icon element */
  icon?: React.ReactNode;
  /** Prevent closing on backdrop click */
  disableBackdropClose?: boolean;
}

/**
 * GenericModal - A reusable dark futuristic modal component
 * 
 * Features:
 * - Dark futuristic design with glassmorphism
 * - Flexible button configuration
 * - Multiple variants (info, warning, error, success)
 * - Customizable content via children or message prop
 * - Mobile responsive
 * - Accessibility support (ESC key, focus trap)
 */
export const GenericModal: React.FC<GenericModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  children,
  buttons = [],
  variant = 'info',
  showCloseButton = true,
  icon,
  disableBackdropClose = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !disableBackdropClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, disableBackdropClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !disableBackdropClose) {
      onClose();
    }
  };

  // Get icon based on variant
  const getVariantIcon = () => {
    if (icon) return icon;

    const iconMap = {
      info: '🔵',
      warning: '⚠️',
      error: '❌',
      success: '✅',
    };

    return (
      <div className={`${styles.iconWrapper}`}>
        <span className={`${styles.icon} ${styles[`icon${variant.charAt(0).toUpperCase() + variant.slice(1)}`]}`}>
          {iconMap[variant]}
        </span>
      </div>
    );
  };

  // Get button style class
  const getButtonClass = (buttonVariant?: string) => {
    switch (buttonVariant) {
      case 'primary':
        return styles.buttonPrimary;
      case 'danger':
        return styles.buttonDanger;
      case 'secondary':
      default:
        return styles.buttonSecondary;
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`${styles.modalOverlay} ${isOpen ? styles.open : ''}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className={styles.modalContainer} ref={modalRef}>
        {/* Close Button */}
        {showCloseButton && (
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close modal"
          >
            ✕
          </button>
        )}

        {/* Header */}
        <div className={styles.modalHeader}>
          {getVariantIcon()}
          <h2 id="modal-title" className={styles.modalTitle}>
            {title}
          </h2>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {children || <p>{message}</p>}
        </div>

        {/* Footer with Buttons */}
        {buttons.length > 0 && (
          <div className={styles.modalFooter}>
            {buttons.map((button, index) => (
              <button
                key={index}
                className={`${styles.button} ${getButtonClass(button.variant)}`}
                onClick={button.onClick}
                disabled={button.disabled}
              >
                {button.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericModal;
